import { MonitorIcon, MoonIcon, SunIcon } from "lucide-react";
import { useState, type ComponentType } from "react";
import { cn } from "../lib/cn";
import { applyThemePreference, readThemePreference, type ThemePreference } from "../lib/theme";

export interface ThemeSwitcherProps {
  /** Translated names of the three states; the component carries no dictionary of its own. */
  labels: Record<ThemePreference, string>;
  /** Accessible name of the group, e.g. "Colour scheme". */
  groupLabel: string;
  className?: string;
}

const OPTIONS: { value: ThemePreference; Icon: ComponentType<{ "aria-hidden"?: boolean }> }[] = [
  { value: "light", Icon: SunIcon },
  { value: "dark", Icon: MoonIcon },
  { value: "system", Icon: MonitorIcon },
];

/**
 * Light, dark, or whatever the operating system says (US-23.6).
 *
 * <p>Three buttons rather than one that cycles: a person who has just been given a dark screen by
 * their laptop at sunset should be able to say "no, light" in one press, and see that they have.
 *
 * <p>State lives on `<html>` and in `localStorage`, not in React — the attribute is set before the
 * first paint by a snippet in index.html, so a provider here would only be a second source of truth.
 * This component is therefore free to sit in any of the three headers with nothing above it.
 */
export function ThemeSwitcher({ labels, groupLabel, className }: ThemeSwitcherProps) {
  const [preference, setPreference] = useState<ThemePreference>(() => readThemePreference());

  const choose = (next: ThemePreference) => {
    applyThemePreference(next);
    setPreference(next);
  };

  return (
    <div
      role="group"
      aria-label={groupLabel}
      data-testid="theme-switcher"
      className={cn("inline-flex items-center rounded-md border border-border bg-card p-0.5", className)}
    >
      {OPTIONS.map(({ value, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => choose(value)}
          aria-pressed={preference === value}
          title={labels[value]}
          aria-label={labels[value]}
          data-testid={`theme-${value}`}
          className={cn(
            "inline-flex size-7 items-center justify-center rounded-sm transition-colors [&_svg]:size-4",
            preference === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Icon aria-hidden />
        </button>
      ))}
    </div>
  );
}
